// Common axle defaults for SRT calculations
// Allows for showing default values in the UI and ensuring consistent defaults across calculations

// MRWA wheel mass table per side
// tyreSize → tyreConfig → wheel mass (kg)
const MRWA_WHEEL_MASS = {
   0: { 0: 50, 1: 70, 2: 100, 3: 200 },
   1: { 0: 75, 1: 105, 2: 150, 3: 300 },
   2: { 0: 100, 1: 140, 2: 200, 3: 400 },
};

// Axle masses
const AXLE_BASE_MASS = {
   0: 50, // Front of simple trailer (special case)
   1: 350, // Truck steer
   2: 700, // Truck drive
   3: 400, // Trailer
};

/** * Returns the default axle height based on tyre size index.
 * Same as Unsprung Mass Cg Height (m)
 *
 * The axle height is taken as approximately half the rolling diameter of the tyre (i.e. the tyre’s loaded radius).
 * Larger tyres give a higher axle centreline from the road:

   A 17.5" rim typically corresponds to ~720 mm overall tyre diameter → ~360 mm radius → 0.36 m axle height.
   A 19.5" rim tyre is usually ~800 mm diameter → ~400 mm radius → 0.40 m axle height.
   A 22.5" rim tyre is typically ~980 mm diameter → ~490 mm radius → 0.49 m axle height.

 Function returns fixed values for these sizes using  a loaded factor of 0.94 to reduce rolling diameter when loaded.
 * @param {number} tyreSizeIndex - Tyre size index (0, 1, 2)
 * @returns {number} - Default axle height in meters
 * @throws {Error} If tyreSizeIndex is invalid
 */
export function getDefaultAxleHeight(tyreSizeIndex) {
   // Default axle heights based on tyre size index in meters
   switch (tyreSizeIndex) {
      case 0: // 17.5
         return 0.36;
      case 1: // 19.5
         return 0.4;
      case 2: // 22.5
         return 0.49;
      default:
         throw new Error(`Invalid tyre size index: ${tyreSizeIndex}`);
   }
}

// Not used optional helper for future use and to illustrate above
// Converts a tyre size string like "265/70R19.5" to an estimated axle (hub) height in metres.
//
export const axleHeightFromTyreSize = (sizeStr, loadedFactor = 0.94) => {
   // Basic validation
   if (typeof sizeStr !== 'string') {
      throw new Error('Tyre size must be a string like "295/80R22.5".');
   }

   // Parse patterns like "265/70R19.5" or "215/75R17.5"
   // Captures: width (mm), aspect (%), rim (inches)
   const m = sizeStr.trim().match(/^(\d{3})\/(\d{2})R(\d{2}(?:\.\d)?)$/i);
   if (!m) {
      throw new Error(`Unrecognised tyre size format: ${sizeStr}`);
   }

   // Extract numeric parts
   const widthMm = Number(m[1]); // e.g. 265
   const aspectPc = Number(m[2]); // e.g. 70
   const rimIn = Number(m[3]); // e.g. 19.5

   // Convert rim inches to millimetres
   const rimMm = rimIn * 25.4; // 1 inch = 25.4 mm

   // Sidewall height (mm) = width * aspect%
   const sidewallMm = widthMm * (aspectPc / 100);

   // Unloaded overall diameter (mm) = rim + 2 * sidewall
   const odMm = rimMm + 2 * sidewallMm;

   // Unloaded radius (mm)
   const urMm = odMm / 2;

   // Static loaded radius (mm) ≈ unloaded radius * loadedFactor (0.92–0.95 typical)
   const slrMm = urMm * loadedFactor;

   // Convert to metres
   const axleHeightM = slrMm / 1000;

   // Return with a little rounding to mm precision to avoid ugly decimals
   return Math.round(axleHeightM * 1000) / 1000;
};

// Examples:
// axleHeightFromTyreSize('215/75R17.5') ≈ 0.360 (with 0.94)
// axleHeightFromTyreSize('265/70R19.5', 0.93) ≈ 0.403
// axleHeightFromTyreSize('295/80R22.5') ≈ 0.490

/** * Returns the default axle width in meters.
 * Same as Maximum Ground Contact Width(m).
 * This is a constant value used across all axle types.
 *
 * @returns {number} - Default axle width in meters
 */
export function getDefaultAxleWidth() {
   // Default axle width in meters
   return 2.42; // In all cases
}

/**
 * Computes unsprung axle mass using tyre size, tyre config, axle type, and formMode.
 * Same as Unsprung Mass (kg).
 *
 * @param {number} tyreSize - Tyre size index (0,1,2)
 * @param {number} tyreConfig - Tyre config index (0–3)
 * @param {number} axleTypeCode - Axle type: 0=simple front, 1=steer, 2=drive, 3=trailer
 * @param {string} formMode - 'legacy' or 'mrwa'
 * @returns {number} Unsprung axle mass in kg, or NaN if invalid input
 */
export function getDefaultAxleMass(tyreSize, tyreConfig, axleTypeCode) {
   if (tyreSize < 0 || tyreSize > 2 || tyreConfig < 0 || tyreConfig > 3 || axleTypeCode < 0 || axleTypeCode > 3) {
      throw new Error(`Invalid input: tyreSize=${tyreSize}, tyreConfig=${tyreConfig}, axleTypeCode=${axleTypeCode}`);
   }
   const wheelMass = MRWA_WHEEL_MASS[tyreSize]?.[tyreConfig];
   const baseMass = AXLE_BASE_MASS[axleTypeCode];
   if (wheelMass === undefined || baseMass === undefined) return NaN;

   // Front of simple trailer special case - just base mass
   if (axleTypeCode === 0) return baseMass;

   // Standard return: base mass + wheel weight each side
   return baseMass + 2 * wheelMass;
}

/* * Returns the default beta for a given vehicle type.
 * beta is the vertical distance between the sprung mass centre of gravity (sprungTareCg) and
 * the unsprung mass centre of gravity (axleHeightTotal), in metres.
 *
 * For a prime mover (tractor) or rigid truck, the sprung body is relatively low compared to trailers (e.g. cab chassis, engine mass, and body structure are closer to the axles).
 * For semi-trailers, dog trailers, logging trailers, etc., the payload sits significantly higher above the axles.
 * The sprung CoG (logs, freight, tanks) is commonly more than a metre above the axle centreline.
 * Calibration against measured tilt-table SRTs led to a higher default β value — about 1.25 m — which gives realistic SRT predictions for trailers
 *
 * @param {string} vehicleType - Normalized vehicle type string (e.g., 'truck', 'semi-trailer')
 * @returns {number} - Default beta value (unitless)
 */

export function getDefaultBeta(vehicleType) {
   const v = vehicleType.toLowerCase();
   if (v === 'tractor') return 0.56;
   if (v === 'truck') return 0.56;
   return 1.25;
}

/* * Returns the default (est) sprung tare CG height based on vehicle type and tyre size index.
 * sprungTareCg is the vertical position of the centre of gravity (CoG) of the sprung (suspension-supported)
 * portion of the unladen vehicle mass (tare mass), measured from the ground.
 * A default value is computed as the axle height + beta (an estimated value based on vehicle type).
 */
// export function getEstimateSprungTareCg(vehicleType, tyreSizeIndex) {
//    const defaultAxleHeight = getDefaultAxleHeight(tyreSizeIndex);
//    const defaultBeta = getDefaultBeta(vehicleType);
//    // Default sprung tare CG = beta + axle height
//    return defaultBeta + defaultAxleHeight;
// }

export function getEstimatedSprungTareCg(
   vehicleType,
   frontTyreSizeIndex,
   rearTyreSizeIndex,
   frontAxleMass,
   rearAxleMass
) {
   const defaultBeta = getDefaultBeta(vehicleType);
   const fHeight = getDefaultAxleHeight(frontTyreSizeIndex);
   const rHeight = getDefaultAxleHeight(rearTyreSizeIndex);

   if (!Number.isFinite(fHeight) && !Number.isFinite(rHeight)) return NaN;

   // Weighted average unsprung CG height
   const totalMass = frontAxleMass + rearAxleMass;
   if (totalMass <= 0) return NaN;

   const weightedSum =
      (Number.isFinite(fHeight) ? fHeight * frontAxleMass : 0) +
      (Number.isFinite(rHeight) ? rHeight * rearAxleMass : 0);

   const axleHeightTotal = weightedSum / totalMass;

   // Estimated sprung mass CG
   return +(defaultBeta + axleHeightTotal).toFixed(2);
}
